import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FaBriefcase, FaCheckCircle, FaCalendarAlt, FaMapMarkerAlt } from 'react-icons/fa';
import { SectionHeader, Card, Badge, AnimatedSection } from '../components/common';
import { personalInfo, experience } from '../data/profileData';

const Experience = () => {
  return (
    <>
      <Helmet>
        <title>Experience | Rakesh Kumar Tripathi</title>
        <meta name="description" content={`Professional experience of ${personalInfo.name}. Over 25+ years of IT leadership across healthcare, education, and technology industries.`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-8 pb-16 gradient-bg-subtle">
        <div className="container-custom">
          <AnimatedSection>
            <SectionHeader
              badge="Career Journey"
              title="Professional Experience"
              subtitle="Over 25+ years of technology leadership across diverse industries, delivering innovative solutions and building high-performing teams."
            />
          </AnimatedSection>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mt-8">
            {[
              { value: '25+', label: 'Years Experience' },
              { value: '3+', label: 'Industries Served' },
              { value: '10+', label: 'Organizations' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center p-4 bg-white rounded-xl shadow-soft"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-2xl md:text-3xl font-bold text-primary-500">{stat.value}</div>
                <div className="text-sm text-dark-500">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Alternating Timeline Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="relative">
            {/* Center vertical line - visible only on desktop */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-primary-500 via-secondary-500 to-primary-500 rounded-full" />

            {/* Mobile vertical line */}
            <div className="md:hidden absolute left-4 h-full w-0.5 bg-gradient-to-b from-primary-500 via-secondary-500 to-primary-500" />

            <div className="space-y-12">
              {experience.map((exp, index) => {
                const isLeft = index % 2 === 0;

                return (
                  <motion.div
                    key={exp.id}
                    className="relative"
                    initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    {/* Desktop Layout */}
                    <div className="hidden md:flex items-start">
                      {/* Left Side Content */}
                      <div className={`w-5/12 ${isLeft ? 'pr-8' : ''}`}>
                        {isLeft && (
                          <div className="text-right">
                            <ExperienceCard exp={exp} alignRight />
                          </div>
                        )}
                      </div>

                      {/* Center Dot */}
                      <div className="w-2/12 flex justify-center">
                        <motion.div
                          className="w-5 h-5 bg-primary-500 rounded-full border-4 border-white shadow-lg z-10 relative mt-6"
                          whileHover={{ scale: 1.3 }}
                        />
                      </div>

                      {/* Right Side Content */}
                      <div className={`w-5/12 ${!isLeft ? 'pl-8' : ''}`}>
                        {!isLeft && (
                          <div className="text-left">
                            <ExperienceCard exp={exp} />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Mobile Layout */}
                    <div className="md:hidden flex">
                      <div className="w-8 flex-shrink-0 flex justify-center">
                        <motion.div
                          className="w-3 h-3 bg-primary-500 rounded-full border-2 border-white shadow-lg z-10 relative mt-6"
                          whileHover={{ scale: 1.2 }}
                        />
                      </div>
                      <div className="flex-1 pl-4">
                        <ExperienceCard exp={exp} />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="section-padding gradient-bg-subtle">
        <div className="container-custom">
          <SectionHeader
            badge="Industries"
            title="Diverse Industry Experience"
            subtitle="Technology leadership across multiple sectors"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                name: 'Healthcare',
                description: 'IT infrastructure, cyber security, and ERPNext implementation for healthcare communications.',
                highlights: ['ERPNext Implementation', 'SOC 2 Compliance', 'Zero Trust Security'],
              },
              {
                name: 'Education',
                description: 'Academic teaching, curriculum development, and IT infrastructure management.',
                highlights: ['Curriculum Design', 'LMS Implementation', 'Student Mentorship'],
              },
              {
                name: 'IT Training',
                description: 'Professional IT training across multiple organizations and technologies.',
                highlights: ['Programming Languages', 'Cyber Security', 'Enterprise Systems'],
              },
            ].map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card elevated className="h-full">
                  <h3 className="text-xl font-bold text-dark-800 mb-2">{industry.name}</h3>
                  <p className="text-dark-500 text-sm mb-4">{industry.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {industry.highlights.map((highlight) => (
                      <Badge key={highlight} variant="secondary" size="sm">
                        {highlight}
                      </Badge>
                    ))}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Roles Summary */}
      <section className="py-16 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { role: 'Head of IT Services', org: 'Turacoz Healthcare', period: '2014 - Present' },
              { role: 'Director Systems', org: 'FIT Group of Institutions', period: '2021 - Present' },
              { role: 'System Manager / Professor', org: 'FIT Group of Institutions', period: '2003 - 2021' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="p-6 bg-white/10 backdrop-blur-sm rounded-xl"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-xl md:text-2xl font-bold text-white mb-2">{item.role}</div>
                <div className="text-white/90 font-medium mb-1">{item.org}</div>
                <div className="text-white/70 text-sm">{item.period}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

// Experience Card Component
const ExperienceCard = ({ exp, alignRight = false }) => {
  return (
    <div className={`bg-white rounded-xl shadow-soft p-6 hover:shadow-medium transition-shadow duration-300 ${alignRight ? 'text-right' : 'text-left'}`}>
      {/* Header */}
      <div className={`flex flex-wrap items-start gap-4 mb-4 ${alignRight ? 'flex-row-reverse justify-start' : 'justify-between'}`}>
        <div className={alignRight ? 'text-right' : ''}>
          <h3 className="text-xl md:text-2xl font-bold text-dark-800">{exp.title}</h3>
          <div className={`flex flex-wrap items-center gap-2 mt-2 ${alignRight ? 'justify-end' : ''}`}>
            <span className="flex items-center text-primary-500 font-medium">
              <FaBriefcase className="w-4 h-4 mr-2" />
              {exp.company}
            </span>
            <span className="flex items-center text-dark-400 text-sm">
              <FaMapMarkerAlt className="w-3 h-3 mr-1" />
              {exp.location}
            </span>
          </div>
        </div>
        <Badge
          variant={exp.current ? 'secondary' : 'primary'}
          size="lg"
          icon={<FaCalendarAlt className="w-3 h-3" />}
        >
          {exp.startDate} - {exp.endDate}
        </Badge>
      </div>

      {/* Description */}
      <p className={`text-dark-500 mb-6 ${alignRight ? 'text-right' : ''}`}>{exp.description}</p>

      {/* Responsibilities */}
      {exp.responsibilities && exp.responsibilities.length > 0 && (
        <div className="mb-6">
          <h4 className={`text-sm font-semibold text-dark-700 uppercase tracking-wider mb-3 ${alignRight ? 'text-right' : ''}`}>
            Key Responsibilities
          </h4>
          <ul className={`space-y-2 ${alignRight ? 'text-right' : ''}`}>
            {exp.responsibilities.slice(0, 4).map((resp, i) => (
              <li key={i} className={`flex items-start space-x-2 ${alignRight ? 'flex-row-reverse space-x-reverse' : ''}`}>
                <FaCheckCircle className="w-4 h-4 text-secondary-500 mt-0.5 flex-shrink-0" />
                <span className="text-dark-600 text-sm">{resp}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Achievements */}
      {exp.achievements && exp.achievements.length > 0 && (
        <div className="mb-6">
          <h4 className={`text-sm font-semibold text-dark-700 uppercase tracking-wider mb-3 ${alignRight ? 'text-right' : ''}`}>
            Key Achievements
          </h4>
          <ul className={`space-y-2 ${alignRight ? 'text-right' : ''}`}>
            {exp.achievements.slice(0, 3).map((achievement, i) => (
              <li key={i} className={`flex items-start space-x-2 ${alignRight ? 'flex-row-reverse space-x-reverse' : ''}`}>
                <span className="w-5 h-5 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {i + 1}
                </span>
                <span className="text-dark-600 text-sm">{achievement}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Technologies */}
      <div>
        <h4 className={`text-sm font-semibold text-dark-700 uppercase tracking-wider mb-3 ${alignRight ? 'text-right' : ''}`}>
          Technologies & Tools
        </h4>
        <div className={`flex flex-wrap gap-2 ${alignRight ? 'justify-end' : ''}`}>
          {exp.technologies.slice(0, 6).map((tech) => (
            <Badge key={tech} variant="outline" size="sm">
              {tech}
            </Badge>
          ))}
          {exp.technologies.length > 6 && (
            <Badge variant="dark" size="sm">
              +{exp.technologies.length - 6} more
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

export default Experience;
